import * as React from 'react';
import styles from "./styles.module.css";
import { Card, Divider } from '@mui/material';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import LineHeroCard from './LineHeroCard';
import LuminaryStatusFilterContainer from './LuminaryStatusFilterContainer';
import ZoneHeroCard from './ZoneHeroCard';
import { DeviceData, LineDetail, ZoneDetail } from '../../../types/device';
import { TooltipItem } from '../../../components/TooltipItem/TooltipItem';
import { getControlledText } from '../../enum/GenericFormat';

export interface Props {
    systemDetail: DeviceData;
    setSelectedLines?: (lines: number[]) => void;
    setSelectedZones?: (zone: number[]) => void;
    setSelectedStatuses?: (status: string[]) => void;
}


const LineZones: React.FunctionComponent<Props> = ({ systemDetail, setSelectedLines = (lines: number[]) => { }, setSelectedZones = (zones: number[]) => { }, setSelectedStatuses }) => {
    const { t } = useLanguageLocale();
    const [selectedLine, setSelectedLine] = React.useState<LineDetail | undefined>(undefined);
    const [selectedZone, setSelectedZone] = React.useState<ZoneDetail | undefined>(undefined);
    const lines = systemDetail.lineDetails || [];

    React.useEffect(() => {
        setSelectedZone(undefined);
    }, [selectedLine]);

    const handleRemoveZoneSelection = () => {
        setSelectedZone(undefined);
    }

    const getZonesForLine = (line: LineDetail) => {
        const allZones: ZoneDetail[] = systemDetail.zoneDetails || [];
        const zonesForLine = allZones.filter(z => z.lineId === line.id);

        const zones: ZoneDetail[] = [];
        for (let i = 0; i < line.zoneCount; i++) {
            const actualZone = zonesForLine[i];
            if (actualZone) {
                zones.push(actualZone);
            } else {
                zones.push({
                    id: i + 1,
                    name: `${t('LINE_ZONE.ZONE')} ${line.id}-${i + 1}`,
                    status: line.bitStatus[i] ?? 0,
                    description: null,
                    luminairesCount: 0,
                    lineId: line.id,
                    bitStatus: Array(line.zoneCount).fill(0),
                });
            }
        }
        return zones;
    };

    React.useEffect(() => {

        if (selectedLine) {
            setSelectedLines([selectedLine.id])
            setSelectedZones([]);
        } else {
            setSelectedLines([]);
            setSelectedZones([]);
        }
        if (selectedZone) {
            setSelectedZones([selectedZone.id])
        } else {
            setSelectedZones([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLine, selectedZone])

    return (
        <Card className={styles.card} data-testid='line-zone-block'>
            <div className={styles.lineZoneContainer} data-testid="line-zone-container">
                {lines.length === 0 && (!systemDetail.zoneDetails || systemDetail.zoneDetails.length === 0) ? (
                    <div className={styles.lineList}>
                        <div className={styles.noDataFound}>{t('NETWORK.NO_DATA_FOUND')}</div>
                    </div>
                ) : !selectedLine ? (
                    <div className={styles.lineList} data-testid="line-list">
                        {lines.map((line) => (
                            <LineHeroCard
                                click={() => setSelectedLine(line)}
                                data={{
                                    id: line.id,
                                    name: line.name,
                                    status: line.status,
                                    description: line.description || t('MANAGE_USERS.NOT_APPLICABLE'),
                                    zoneCount: line.zoneCount,
                                    luminairesCount: line.luminairesCount,
                                    bitStatus: line.bitStatus,
                                }}
                                key={line.id}
                            />
                        ))}
                    </div>
                ) :
                    selectedLine &&
                    (<div className={styles.lineZoneList}>
                        <div className={styles.selectedLineBlock}>
                            <LineHeroCard
                                click={() => setSelectedLine(undefined)}
                                data={selectedLine}
                            />
                        </div>
                        <div className={styles.lineZonesContainer}>
                            {selectedZone && <div className={styles.lineZoneSelectionBlock} onClick={handleRemoveZoneSelection}></div>}
                            {
                                !selectedZone ?
                                    <>
                                        <h3 style={{ margin: 0 }}>{`${t('DEVICE_DETAILS.ZONES')} (${selectedLine ? selectedLine?.zoneCount : 0})`}</h3>
                                    </>
                                    :
                                    selectedZone &&
                                    <>
                                        <h3 style={{ margin: 0, zIndex: 1, width: 'fit-content' }}>{`Zone ${selectedZone.id}`}</h3>
                                        <TooltipItem
                                            title={selectedZone.name || t('MANAGE_USERS.NOT_APPLICABLE')}
                                            isTopPlacement={true}
                                            lenghtLimit={30}
                                            component={
                                                <h4 style={{ margin: 0, fontWeight: 'normal', zIndex: 1, width: 'fit-content' }}>
                                                    {getControlledText(selectedZone.name || t('MANAGE_USERS.NOT_APPLICABLE'), 30)}
                                                </h4>
                                            }
                                        />
                                        <TooltipItem
                                            title={selectedZone.description || t('MANAGE_USERS.NOT_APPLICABLE')}
                                            isTopPlacement={true}
                                            lenghtLimit={30}
                                            component={
                                                <h4 style={{ margin: 0, fontWeight: 'normal', zIndex: 1, width: 'fit-content' }}>
                                                    {getControlledText(selectedZone.description || t('MANAGE_USERS.NOT_APPLICABLE'), 30)}
                                                </h4>
                                            }
                                        />
                                        <h5 style={{ margin: 0, fontWeight: 'normal', fontSize: 14, zIndex: 1 }}>{`${selectedZone.luminairesCount} ${t('ATPLUS.ATPLUS_LUMINAIRE')}`}</h5>
                                    </>
                            }
                            <div className={styles.zoneList}>
                                {
                                    getZonesForLine(selectedLine).map((zone, i) => {
                                        const zoneForSelection: ZoneDetail = {
                                            ...zone,
                                            status: zone.status,
                                        };
                                        return (
                                            <ZoneHeroCard
                                                click={() => setSelectedZone(zoneForSelection)}
                                                data={{
                                                    id: zone.id,
                                                    name: zone.name,
                                                    status: zone.status,
                                                    description: zone.description || t('MANAGE_USERS.NOT_APPLICABLE'),
                                                    luminairesCount: zone.luminairesCount,
                                                    lineId: selectedLine.id,
                                                    bitStatus: zone.bitStatus,
                                                }}
                                                key={i}
                                                isSelected={zone.id === selectedZone?.id}
                                            />
                                        );
                                    })
                                }
                            </div>
                        </div>
                    </div>)
                }
            </div>
            <Divider />
            <LuminaryStatusFilterContainer
                lines={lines}
                selectedLine={selectedLine}
                setSelectedLine={setSelectedLine}
                selectedZone={selectedZone}
                setSelectedZone={setSelectedZone}
                zones={selectedLine ? getZonesForLine(selectedLine) : []}
                luminaryCount={systemDetail.luminaryCount ?? 0}
                setSelectedStatuses={setSelectedStatuses}
            />
        </Card>
    );
};

export default LineZones;
